import { useTranslation } from 'utils/translation';
import {
  Navigation,
  NavigationItem,
} from '../../components/v2/Navigation/Navigation';
import Icon from '../../components/v2/Icon/Icon';
import { Sidebar, SidebarNotice } from '../../components/v2/Sidebar/Sidebar';
import React from 'react';
import SidebarSearch from '../../components/v2/Sidebar/SidebarSearch';
import usePermissions from '../../modules/auth/permissions';
import useReports from '../../modules/reports/hooks/use-reports';
import { useLocation, useNavigate } from 'utils/router';
import SystemSummary from '../../modules/ui/components/SystemSummary';
import { ReportNavItem } from './ReportNavItem';
import { encode } from '../../utils/state-encoder';
import { route } from 'router';
import { useAppSettings } from 'modules/ui/hooks';
import config from 'config/app';
import Spacer from 'components/v2/Spacer/Spacer';
import { applyFilters, Filters } from '../../core';
import Divider from '../../components/v2/Divider/Divider';
import useBasketModal from '../../modules/basket/hooks/useBasketModal';
import { useBasketItems } from '../../modules/basket/hooks';
import Badge from '../../components/v2/Badge/Badge';
import { useAppFeatures } from '../../modules/ui/selectors/app';
import DemoRequestCallToAction from '../../components/v2/DemoRequest/DemoRequestCallToAction';
import { useCurrentUser } from '../../modules/auth/hooks';

export { ReportNavItem };

const DashboardSidebar = () => {
  const { can } = usePermissions();
  const user = useCurrentUser();
  const { t } = useTranslation();
  const { data: reports = [], isLoading } = useReports();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useAppSettings();
  const features = useAppFeatures();
  const hasAssets =
    features.devices && applyFilters(Filters.assets.navigation.assets, true);
  const navbarExtraItems = applyFilters(Filters.navbar.extraItems, null);
  const openBasketModal = useBasketModal();
  const { itemsCount } = useBasketItems();

  const isActive = (path: string) => {
    return location.pathname.startsWith(`/assets/${path}`);
  };

  const handleSearch = React.useCallback(
    (search: string) => {
      const filters = encode({ search });
      navigate(`/assets/search?filters=${filters}`);
    },
    [navigate],
  );

  if (isLoading) {
    return null;
  }

  const sharedReports = reports.filter((item) => !item.isOwner);
  const ownReports = reports.filter((item) => item.isOwner);
  const compositesLabel = applyFilters(
    Filters.composites.labelPlural,
    t('Asset Clusters'),
  );

  return (
    <Sidebar>
      {/*<SidebarSearch*/}
      {/*  isActive={isActive('search')}*/}
      {/*  placeholder={t('report_search')}*/}
      {/*  onChange={handleSearch}*/}
      {/*/>*/}
      <Navigation>
        {hasAssets && (
          <NavigationItem
            onClick={
              isActive('report')
                ? undefined
                : () => navigate(route('report.index', { id: reports[0]?.id }))
            }
            title={t('Assets')}
            icon={<Icon name={'far fa-object-subtract'} />}
            isActive={isActive('report')}
          >
            <div title={t('report.shared_lists_tooltip')}>
              {sharedReports.map((report) => (
                <ReportNavItem report={report} key={report.id} />
              ))}
            </div>
            {ownReports.length > 0 && (
              <>
                <Spacer size={2} />
                <Divider variant="dashed" />
                <Spacer size={2} />
              </>
            )}
            {ownReports.map((report) => (
              <ReportNavItem report={report} key={report.id} />
            ))}
          </NavigationItem>
        )}
        {hasAssets &&
          applyFilters(Filters.assets.navigation.assetTypes, true) && (
            <NavigationItem
              onClick={() => navigate('/assets/types')}
              isActive={isActive('types')}
              title={t('Asset Types')}
              icon={<Icon name={'far fa-object-intersect'} />}
            />
          )}
        {hasAssets &&
          applyFilters(Filters.assets.navigation.assetCategories, true) &&
          settings.features.deviceGroups &&
          can('view device groups') && (
            <NavigationItem
              onClick={() => navigate('/assets/groups')}
              title={t('Asset Categories')}
              isActive={isActive('groups')}
              icon={<Icon name={'far fa-object-union'} />}
            />
          )}
        {hasAssets &&
          applyFilters(Filters.assets.navigation.assetClusters, true) && (
            <NavigationItem
              onClick={() => navigate('/assets/composites')}
              isActive={isActive('composites')}
              title={compositesLabel}
              icon={<Icon name={'far fa-grid-round'} />}
            />
          )}
        {can('view service requests') &&
          applyFilters(Filters.assets.navigation.serviceTickets, true) && (
            <NavigationItem
              title={t('Service Tickets')}
              onClick={() => navigate('/service-tickets')}
              icon={<Icon name={'far fa-screwdriver-wrench'} />}
              isActive={location.pathname.startsWith('/service-tickets')}
            />
          )}
        {navbarExtraItems}
        {can('view system log') && settings.features.systemLogBook && (
          <NavigationItem
            title={t('navigation_system_log')}
            onClick={() => navigate('/logs')}
            icon={<Icon name={'far fa-file-lines'} />}
          />
        )}
        {applyFilters(Filters.assets.navigation.basket, true) && (
          <NavigationItem
            title={
              <Badge expand badgeContent={itemsCount}>
                {t('Basket')}
              </Badge>
            }
            onClick={openBasketModal}
            icon={<Icon name={'fas fa-files'} />}
          />
        )}
      </Navigation>
      <Spacer size={2} grow />
      {!user.hasEnabledSubAccount && features.marketing.demoRequestCta && (
        <>
          <DemoRequestCallToAction />
          <Spacer size={2} />
        </>
      )}
      <SidebarNotice>
        <SystemSummary />
      </SidebarNotice>
    </Sidebar>
  );
};

export default DashboardSidebar;
